import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Input,
  Table,
  Space,
  Tooltip,
  message,
  Popconfirm,
} from "antd";
import dayjs from "dayjs";
import {
  PlusOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import axiosInstance, { baseURL } from "../../../configs"; // sesuaikan path
import { getUser } from "../../../utils/DataUser";
import { canEdit, canDelete, MODULES } from "../../../utils/rbac";

const { Title, Text } = Typography;

const defaultPageSize = 20;

export default function BrowseItem() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
  });

  // filter
  const [searchName, setSearchName] = useState("");
  const [searchCode, setSearchCode] = useState("");

  const [deletingId, setDeletingId] = useState(null);

  const fetchItems = async (page = 1, pageSize = defaultPageSize) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/outgoing", {
        params: {
          page,
          limit: pageSize,
          customer_name: searchName || undefined,
          document_number: searchCode || undefined,
        },
      });

      const raw = res.data || {};
      const payload = raw.data || raw;
      const rows =
        payload.items ||
        payload.rows ||
        payload.data ||
        (Array.isArray(payload) ? payload : []);

      const user = getUser();
      let finalRows = rows;
      if (user && user.id_level === 7) {
        finalRows = rows.filter(
          (item) =>
            item.id_customer === user.username ||
            item.nama_pemilik === user.username ||
            item.customer_name === user.username,
        );
      }

      const total =
        raw.pagination?.total || payload.total || payload.count || rows.length;

      setItems(finalRows);
      setPagination({
        current: page,
        pageSize,
        total: total || 0,
      });
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar pengeluaran barang");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1, defaultPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchItems(1, pagination.pageSize);
  };

  const handleResetFilter = () => {
    setSearchName("");
    setSearchCode("");
    fetchItems(1, defaultPageSize);
  };

  const handleTableChange = (pager) => {
    fetchItems(pager.current, pager.pageSize);
  };

  const handleExportExcel = () => {
    window.open(`${baseURL}/outgoing/export`, "_blank");
  };

  const handleDelete = async (record) => {
    setDeletingId(record.id_outgoing);
    try {
      await axiosInstance.delete(`/outgoing/${record.id_outgoing}`);
      message.success("Pengeluaran barang berhasil dihapus");
      // reload current page
      fetchItems(pagination.current, pagination.pageSize);
    } catch (err) {
      console.error(err);
      message.error("Gagal menghapus pengeluaran barang");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (value) =>
    value ? dayjs(value).format("DD-MM-YYYY") : "-";

  const columns = [
    {
      title: "NO",
      dataIndex: "index",
      width: 70,
      align: "center",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "NO SURAT JALAN",
      dataIndex: "delivery_number",
      width: 130,
    },
    {
      title: "TANGGAL SURAT JALAN",
      dataIndex: "delivery_date",
      width: 140,
      render: (value) => formatDate(value),
    },
    {
      title: "KODE DOKUMEN",
      dataIndex: "id_document",
      width: 130,
      render: (value) => value || "-",
    },
    {
      title: "NO DOKUMEN",
      dataIndex: "document_number",
      width: 160,
      render: (value) => value || "-",
    },
    {
      title: "TANGGAL DOKUMEN",
      dataIndex: "document_date",
      width: 150,
      render: (value) => formatDate(value),
    },
    {
      title: "PEMILIK BARANG",
      dataIndex: "nama_pemilik",
      width: 160,
      render: (value, record) =>
        value || record.id_customer ? value || record.id_customer : "-",
    },

    {
      title: "AKSI",
      key: "action",
      width: 160,
      align: "center",
      render: (_, record) => (
        <Space>
          {canEdit(MODULES.INVENTORY) && (
            <Tooltip title={getUser().id_level === 7 ? "Lihat Detil" : "Edit"}>
              <Link to={`/inventory/updateoutgoing/${record.id_outgoing}`}>
                <Button
                  size="small"
                  type="primary"
                  icon={
                    getUser().id_level === 7 ? (
                      <SearchOutlined />
                    ) : (
                      <EditOutlined />
                    )
                  }
                  style={{
                    background: "#3699FF",
                    borderColor: "#3699FF",
                  }}
                />
              </Link>
            </Tooltip>
          )}

          {canDelete(MODULES.INVENTORY) && getUser().id_level !== 7 && (
            <Popconfirm
              title="Hapus Pengeluaran Barang"
              description={`Yakin ingin menghapus "${
                record.sj_number || record.pemilik
              }"?`}
              okText="Ya, Hapus"
              cancelText="Batal"
              okButtonProps={{
                danger: true,
                loading: deletingId === record.id_outgoing,
              }}
              onConfirm={() => handleDelete(record)}
            >
              <Tooltip title="Hapus">
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  loading={deletingId === record.id_outgoing}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      {/* HEADER TITLE + ACTIONS */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 4 }}>
            Browse Pengeluaran Barang
          </Title>
          <Text type="secondary">
            Kelola daftar Pengeluaran Barang untuk kebutuhan inventory &
            kepabeanan.
          </Text>
        </Col>
        <Col>
          <Space>
            {canEdit(MODULES.INVENTORY) && getUser().id_level !== 7 && (
              <Link to="/inventory/addoutgoing" style={{ color: "#fff" }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  style={{
                    background: "#1BC5BD",
                    borderColor: "#1BC5BD",
                    fontWeight: 600,
                  }}
                >
                  Tambah
                </Button>
              </Link>
            )}
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              style={{
                background: "#1E1EFA",
                borderColor: "#1E1EFA",
                fontWeight: 600,
              }}
            >
              Excel
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Text strong>Cari Berdasarkan Pemilik Barang</Text>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Cari Item..."
            style={{ marginTop: 4 }}
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>

        <Col xs={24} md={12}>
          <Text strong>Cari Berdasarkan No Pendaftaran</Text>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Cari kode..."
            style={{ marginTop: 4 }}
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>

        <Col xs={24} style={{ textAlign: "right" }}>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleResetFilter}>
              Reset
            </Button>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              style={{
                background: "#8950FC",
                borderColor: "#8950FC",
              }}
            >
              Apply Filter
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        style={{ marginTop: 36 }}
        rowKey="id_item"
        dataSource={items}
        columns={columns}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `Total ${total} data | ${range[0]}-${range[1]}`,
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
}
